import { createStore } from "devextreme-aspnet-data-nojquery";
import DataSource from "devextreme/data/data_source";
import {
  setupRequestAsync,
  fetchApi,
  fetchApiOrThrow,
  downloadFileApi,
} from "~/utils";
import { squireApiConfig } from "~/config";

const { url } = squireApiConfig;

function unwrapLabourHoursSummary(result) {
  const groups = result?.$values ?? result;
  if (!Array.isArray(groups)) return groups;
  return groups.map(group => ({
    ...group,
    items: group.items?.$values ?? group.items,
  }));
}

export class ProjectEditorApiService {
  getConstructionTypesDataSource() {
    return new DataSource({
      store: createStore({
        key: "id",
        loadUrl: `${url}/v1/project-editor/construction-types`,
        onBeforeSend: async (method, ajaxOptions) =>
          await setupRequestAsync(method, ajaxOptions),
      }),
      sort: "value",
    });
  }

  getIndustryTypesDataSource() {
    return new DataSource({
      store: createStore({
        key: "id",
        loadUrl: `${url}/v1/project-editor/industry-types`,
        onBeforeSend: async (method, ajaxOptions) =>
          await setupRequestAsync(method, ajaxOptions),
      }),
      sort: "value",
    });
  }

  getJobTypesDataSource() {
    return createStore({
      key: "id",
      loadUrl: `${url}/v1/project-editor/job-types/`,
      onBeforeSend: async (method, ajaxOptions) =>
        await setupRequestAsync(method, ajaxOptions),
    });
  }

  getContractTypesDataSource() {
    return new DataSource({
      store: createStore({
        key: "id",
        loadUrl: `${url}/v1/project-editor/contract-types`,
        onBeforeSend: async (method, ajaxOptions) =>
          await setupRequestAsync(method, ajaxOptions),
      }),
      sort: "value",
    });
  }

  getProjectContractTypesDataSource() {
    return new DataSource({
      store: createStore({
        key: "id",
        loadUrl: `${url}/v1/project-editor/project-contract-types`,
        onBeforeSend: async (method, ajaxOptions) =>
          await setupRequestAsync(method, ajaxOptions),
      }),
      sort: "value",
    });
  }

  getActiveClientsDataSource() {
    return createStore({
      key: "id",
      loadUrl: `${url}/v1/project-editor/active-clients`,
      onBeforeSend: async (method, ajaxOptions) =>
        await setupRequestAsync(method, ajaxOptions),
    });
  }

  getCompaniesDataSource() {
    return createStore({
      key: "id",
      loadUrl: `${url}/v1/project-editor/companies`,
      onBeforeSend: async (method, ajaxOptions) =>
        await setupRequestAsync(method, ajaxOptions),
    });
  }

  getEstimatingCompaniesDataSource() {
    return createStore({
      key: "id",
      loadUrl: `${url}/v1/project-editor/estimating-companies`,
      onBeforeSend: async (method, ajaxOptions) =>
        await setupRequestAsync(method, ajaxOptions),
    });
  }

  getConstructionCompaniesDataSource() {
    return createStore({
      key: "id",
      loadUrl: `${url}/v1/project-editor/construction-companies`,
      onBeforeSend: async (method, ajaxOptions) =>
        await setupRequestAsync(method, ajaxOptions),
    });
  }

  getLandlordCompaniesDataSource() {
    return createStore({
      key: "id",
      loadUrl: `${url}/v1/project-editor/landlord-companies`,
      onBeforeSend: async (method, ajaxOptions) =>
        await setupRequestAsync(method, ajaxOptions),
    });
  }

  getBillingCompaniesDataSource() {
    return createStore({
      key: "id",
      loadUrl: `${url}/v1/project-editor/billing-companies`,
      onBeforeSend: async (method, ajaxOptions) =>
        await setupRequestAsync(method, ajaxOptions),
    });
  }

  getOpportunitiesDataSource() {
    return createStore({
      key: "id",
      loadUrl: `${url}/v1/project-editor/opportunities`,
      onBeforeSend: async (method, ajaxOptions) =>
        await setupRequestAsync(method, ajaxOptions),
    });
  }

  getTemplatesDataSource() {
    return new DataSource({
      store: createStore({
        key: "id",
        loadUrl: `${url}/v1/project-editor/templates`,
        onBeforeSend: async (method, ajaxOptions) =>
          await setupRequestAsync(method, ajaxOptions),
      }),
      sort: "name",
    });
  }

  async getOpportunityDetails(opportunityId) {
    const fetchUrl = `${url}/v1/project-editor/opportunity-details/${opportunityId}`;
    return await fetchApi(fetchUrl, "GET");
  }

  async createOpportunity(values) {
    const fetchUrl = `${url}/v1/project-editor/create-opportunity`;
    const formBody = new URLSearchParams({
      values: JSON.stringify(values),
    });
    await fetchApi(fetchUrl, "POST", formBody);
  }

  async updateOpportunity(opportunityId, values) {
    const fetchUrl = `${url}/v1/project-editor/update-opportunity`;
    const formBody = new URLSearchParams({
      key: opportunityId,
      values: JSON.stringify(values),
    });
    await fetchApi(fetchUrl, "PUT", formBody);
  }

  async getEstimateDetails(estimateId) {
    const fetchUrl = `${url}/v1/project-editor/estimate-details/${estimateId}`;
    return await fetchApi(fetchUrl, "GET");
  }

  // Sales tax codes imported from Sage (WK-541). Read-only — Squire never writes them. Sorted by the
  // Sage SortOrder server-side, so no client sort here.
  getTaxCodesStore() {
    return createStore({
      key: "id",
      loadUrl: `${url}/v1/tax-codes/`,
      onBeforeSend: async (method, ajaxOptions) =>
        await setupRequestAsync(method, ajaxOptions),
    });
  }

  // DataSource wrapper for Form/SelectBox editors. A grid <Lookup> column must use the raw store
  // instead (getTaxCodesStore) — handing it a DataSource instance throws E1016.
  getTaxCodesDataSource() {
    return new DataSource({ store: this.getTaxCodesStore() });
  }

  async createEstimate(values) {
    const fetchUrl = `${url}/v1/project-editor/create-estimate`;
    const formBody = new URLSearchParams({
      values: JSON.stringify(values),
    });
    await fetchApi(fetchUrl, "POST", formBody);
  }

  async updateEstimate(estimateId, values) {
    const fetchUrl = `${url}/v1/project-editor/update-estimate`;
    const formBody = new URLSearchParams({
      key: estimateId,
      values: JSON.stringify(values),
    });
    await fetchApi(fetchUrl, "PUT", formBody);
  }

  // --- Post-submit project clients & contacts (immediate CRUD, like estimate items) ---
  // Each add/remove/change persists on its own call; the caller refetches estimate-details after.
  // Writes use fetchApiOrThrow so a rejected domain guard (e.g. can't delete the sole landlord)
  // surfaces instead of being silently swallowed.

  getLandlordContactTypesDataSource() {
    return createStore({
      key: "id",
      loadUrl: `${url}/v1/lookups/LandlordContactType`,
      onBeforeSend: async (method, ajaxOptions) =>
        await setupRequestAsync(method, ajaxOptions),
    });
  }

  getBillingAddressesByCompanyDataSource(companyId) {
    return createStore({
      key: "id",
      loadUrl: `${url}/v1/project-editor/${companyId}/billing-addresses`,
      onBeforeSend: async (method, ajaxOptions) =>
        await setupRequestAsync(method, ajaxOptions),
    });
  }

  async createProjectClient(projectId, values) {
    const fetchUrl = `${url}/v1/project-editor/${projectId}/clients`;
    const formBody = new URLSearchParams({
      values: JSON.stringify(values),
    });
    return await fetchApiOrThrow(fetchUrl, "POST", formBody);
  }

  async updateProjectClient(projectId, projectClientId, values) {
    const fetchUrl = `${url}/v1/project-editor/${projectId}/clients`;
    const formBody = new URLSearchParams({
      key: projectClientId,
      values: JSON.stringify(values),
    });
    return await fetchApiOrThrow(fetchUrl, "PUT", formBody);
  }

  async deleteProjectClient(projectId, projectClientId) {
    const fetchUrl = `${url}/v1/project-editor/${projectId}/clients`;
    const formBody = new URLSearchParams({
      key: projectClientId,
    });
    return await fetchApiOrThrow(fetchUrl, "DELETE", formBody);
  }

  async setLandlordClient(projectId, values) {
    const fetchUrl = `${url}/v1/project-editor/${projectId}/landlord`;
    const formBody = new URLSearchParams({
      values: JSON.stringify(values),
    });
    return await fetchApiOrThrow(fetchUrl, "PUT", formBody);
  }

  async createProjectContact(projectId, projectClientId, values) {
    const fetchUrl = `${url}/v1/project-editor/${projectId}/clients/${projectClientId}/contacts`;
    const formBody = new URLSearchParams({
      values: JSON.stringify(values),
    });
    return await fetchApiOrThrow(fetchUrl, "POST", formBody);
  }

  async updateProjectContact(projectId, projectClientId, projectContactId, values) {
    const fetchUrl = `${url}/v1/project-editor/${projectId}/clients/${projectClientId}/contacts`;
    const formBody = new URLSearchParams({
      key: projectContactId,
      values: JSON.stringify(values),
    });
    return await fetchApiOrThrow(fetchUrl, "PUT", formBody);
  }

  async deleteProjectContact(projectId, projectClientId, projectContactId) {
    const fetchUrl = `${url}/v1/project-editor/${projectId}/clients/${projectClientId}/contacts`;
    const formBody = new URLSearchParams({
      key: projectContactId,
    });
    return await fetchApiOrThrow(fetchUrl, "DELETE", formBody);
  }

  getEstimateItemsDataSource(estimateId) {
    return createStore({
      key: "id",
      loadUrl: `${url}/v1/project-editor/${estimateId}/estimate-items`,
      updateUrl: `${url}/v1/project-editor/${estimateId}/estimate-items`,
      deleteUrl: `${url}/v1/project-editor/${estimateId}/estimate-items`,
      onBeforeSend: async (method, ajaxOptions) =>
        await setupRequestAsync(method, ajaxOptions)
    });
  }

  // The estimate sheet names the manually-accepted items (WK-562) in its confirmation dialog before
  // a bulk refresh skips them. Read the project's items and filter here rather than off the grid:
  // the grid groups and virtualizes its rows, so what it holds in memory is neither flat nor
  // guaranteed complete.
  //
  // Deliberately NOT a server-side `?filter=`: GetEstimateItemsForProject ends in
  // `.Select(ei => new EstimateItemDetailsViewModel().PopulateFromDomainObject(ei))`, which EF can
  // only evaluate on the client. That is fine as a final projection — it is why an unfiltered load
  // works — but DataSourceLoader stacking a Where on top of it cannot be translated, and the request
  // 500s. The grid itself has the same constraint and filters client-side (remoteOperations is off),
  // so this matches how every other read of this endpoint already behaves.
  async getManuallyAcceptedEstimateItems(estimateId) {
    const fetchUrl = `${url}/v1/project-editor/${estimateId}/estimate-items`;
    const result = await fetchApiOrThrow(fetchUrl, "GET");
    // LoadResult shapes vary with the options sent (a bare array when none are), and Newtonsoft wraps
    // collections in $values — so unwrap defensively rather than assume one shape.
    const payload = result?.data ?? result;
    const items = payload?.$values ?? payload ?? [];
    return Array.isArray(items) ? items.filter((item) => item.ratesManuallyAccepted) : [];
  }

  getEstimateScopesDataSource() {
    return createStore({
      key: "id",
      loadUrl: `${url}/v1/project-editor/scopes`,
      onBeforeSend: async (method, ajaxOptions) =>
        await setupRequestAsync(method, ajaxOptions),
    });
  }

  async getSiteLabourHoursSummary(projectId) {
    const fetchUrl = `${url}/v1/project-editor/${projectId}/site-labour-hours-summary`;
    const result = await fetchApi(fetchUrl, "GET");
    return unwrapLabourHoursSummary(result);
  }

  async getManagementLabourHoursSummary(projectId) {
    const fetchUrl = `${url}/v1/project-editor/${projectId}/management-labour-hours-summary`;
    const result = await fetchApi(fetchUrl, "GET");
    return unwrapLabourHoursSummary(result);
  }

  async getEstimateItemForEditing(estimateItemId) {
    const fetchUrl = `${url}/v1/project-editor/estimate-item/${estimateItemId}`;
    return await fetchApi(fetchUrl, "GET");
  }

  async updateEstimateItem(estimateId, estimateItemId, values) {
    const fetchUrl = `${url}/v1/project-editor/${estimateId}/estimate-items`;
    const formBody = new URLSearchParams({
      key: estimateItemId,
      values: JSON.stringify(values),
    });
    return await fetchApi(fetchUrl, "PUT", formBody);
  }

  async updateEstimateItemNotes(estimateId, estimateItemId, notes) {
    const fetchUrl = `${url}/v1/project-editor/${estimateId}/estimate-items`;
    const formBody = new URLSearchParams({
      key: estimateItemId,
      values: `{"notes":"${notes}"}`
    });
    return await fetchApi(fetchUrl, "PUT", formBody);
  }

  // --- Rate confirmation / refresh (WK-551) ---
  // Refresh re-pulls the item's rates from its source calculation and re-snapshots the confirmation
  // dates, so it must surface a failed domain guard rather than swallow it (fetchApiOrThrow).

  async refreshEstimateItemRates(estimateItemId) {
    const fetchUrl = `${url}/v1/project-editor/estimate-item/${estimateItemId}/refresh-rates`;
    return await fetchApiOrThrow(fetchUrl, "PUT");
  }

  async refreshAllEstimateItemRates(projectId) {
    const fetchUrl = `${url}/v1/project-editor/${projectId}/refresh-rates`;
    return await fetchApiOrThrow(fetchUrl, "PUT");
  }

  // WK-540: re-seed the hours of the project's timeline-sourced items after its timeline changed.
  // resetAllOverrides=false keeps hand-overridden items; true replaces them too.
  async refreshEstimateItemsForProjectTimeline(projectId, resetAllOverrides) {
    const fetchUrl = `${url}/v1/project-editor/${projectId}/refresh-timeline-hours`;
    const formBody = new URLSearchParams({ resetAllOverrides: String(resetAllOverrides) });
    return await fetchApiOrThrow(fetchUrl, "PUT", formBody);
  }

  // The references justifying an item's rates are read live off the item's source calculation's
  // scope — the item snapshots the dates, never the references (see GetEstimateScopeReferencesForEstimateItem).
  getEstimateItemReferencesDataSource(estimateItemId) {
    return createStore({
      key: "id",
      loadUrl: `${url}/v1/estimate-scope-references/for-estimate-item/${estimateItemId}`,
      onBeforeSend: async (method, ajaxOptions) =>
        await setupRequestAsync(method, ajaxOptions),
    });
  }

  // The bands of a Banded L&M calculation, so the estimate-sheet calc editor can re-resolve which band
  // applies when the estimator changes the quantity. Rate-style banded calcs need no equivalent: their
  // bands are copied onto the item as NNN_* calculation values when the calculation is assigned.
  getCalculationBandsDataSource(calculationId) {
    return createStore({
      key: "id",
      loadUrl: `${url}/v1/project-editor/calculation/${calculationId}/bands`,
      onBeforeSend: async (method, ajaxOptions) =>
        await setupRequestAsync(method, ajaxOptions),
    });
  }

  async downloadEstimateScopeReference(referenceId, fileName) {
    // Binary response, so it bypasses fetchApi's JSON/text handling (see download-file-api).
    const fetchUrl = `${url}/v1/estimate-scope-references/${referenceId}/download`;
    return await downloadFileApi(fetchUrl, fileName);
  }

  getSubtradesDataSource() {
    return createStore({
      key: "id",
      loadUrl: `${url}/v1/project-editor/subtrades`,
      onBeforeSend: async (method, ajaxOptions) =>
        await setupRequestAsync(method, ajaxOptions),
    });
  }

  async addEstimateScopes(estimateId, ids) {
    const fetchUrl = `${url}/v1/project-editor/add-scopes`;
    const formBody = new URLSearchParams({
      estimateId: estimateId,
      idsToAdd: ids.toString(),
    });
    return await fetchApi(fetchUrl, "POST", formBody);
  }

  getCityNamesDataSource() {
    return createStore({
      key: "city",
      loadUrl: `${url}/v1/project-editor/city-names/`,
      onBeforeSend: async (method, ajaxOptions) =>
        await setupRequestAsync(method, ajaxOptions),
    });
  }

  getProvincesDataSource() {
    return createStore({
      key: "id",
      loadUrl: `${url}/v1/project-editor/provinces/`,
      onBeforeSend: async (method, ajaxOptions) =>
        await setupRequestAsync(method, ajaxOptions),
    });
  }

  async deleteMultipleEstimateScopes(ids) {
    const fetchUrl = `${url}/v1/project-editor/delete-multiple-scopes`;
    const formBody = new URLSearchParams({
      idsToDelete: ids.toString(),
    });
    return await fetchApi(fetchUrl, "DELETE", formBody)
  }
}
